import React, {
  useRef,
  useLayoutEffect,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useId,
} from 'react';

// MUI Components
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import { Box, Divider, IconButton, Input, Tooltip, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';

// Components
import TruncateTextFixedLength from '../TruncatedText';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';
import OlcTooltip from '~/components/General/OlcTooltip';

// Assets
import {
  CheckedCIcon,
  MultiSelectArrowCIcon,
  MultiSelectCrossCIcon,
  UncheckedCIcon,
} from '~/assets/images';

import { focusAdjacentField } from '~/utils/focus-navigation';
import {
  getDropdownMenuWidth,
  getDropdownMenuWidthStyles,
  getDropdownSearchPlaceholder,
} from '~/utils/dropdown-menu';

// Styles
import './styles.scss';

// Constants
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const CREATE_TAG_ID = '-1';
const CREATE_TAG_TITLE = 'Create New Tag';
const NO_TAGS_ASSIGNED_TITLE = 'No Tags Assigned';
const TAG_CREATE_COMPONENT = 'tag-create';
const TAG_CREATE_CONTACT_COMPONENT = 'tag-create-contact';
const PLACEHOLDER_OPTION_ID = '__placeholder_option__';

const MenuProps = {
  PaperProps: {
    style: {
      maxWidth: 'calc(100vw - 32px)',
      overflow: 'hidden',
    },
  },
};

const MultiSelect = (props) => {
  const {
    options = [],
    selectedValue = [],
    setSelectedValue,
    component,
    simpleSelect = false,
    multiple = true,
    placeHolderText = '',
    searchFieldPlaceholderText = '',
    noOptionText = 'No options',
    clearable = false,
    disabled = false,
    searchAutocomplete = false,
    type = '',
    setCreateTagModal = () => {},
    customIcon = false,
    className = '',
    menuPaperClassName = '',
    activeState = false,
    selectedPreviewLimit = 0,
    placeholderOptionText = '',
    inlineMenu = false,
    maxRenderedOptions = 0,
    getOptionLabel,
    // Optional callback to notify parent about search text changes
    onSearchChange = () => {},
    searchOnOpen = false,
    searchFocusRequest = 0,
    // Optional external control over search loading indicator
    searchLoading = undefined,
  } = props;

  // Helper to check if component uses ID-based values
  const usesIdBasedValues = useMemo(
    () => component === 'multipleIds' || component === 'tag' || component === 'updateTags',
    [component]
  );

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const selectId = useId();
  const searchInputId = `${selectId}-search`;
  const selectWrapperRef = useRef(null);
  const menuListRef = useRef(null);
  const fieldRootRef = useRef(null);
  const scrollTopRef = useRef(0);

  const [search, setSearch] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const searchInputRef = useRef(null);
  const lastSearchFocusRequestRef = useRef(0);
  const [internalSearchLoading, setInternalSearchLoading] = useState(false);

  // Store snapshot of selected values when menu opens - used to maintain item positions
  const selectedValuesSnapshotRef = useRef([]);
  const shouldShowDividerRef = useRef(false);
  // Store snapshot of options when menu opens - used to detect newly added options
  const optionsSnapshotRef = useRef([]);
  // Track if we have a valid snapshot to use (prevents glitch during close animation)
  // Using state with delay on close to allow animation to complete
  const [hasSnapshot, setHasSnapshot] = useState(false);
  const snapshotClearTimeoutRef = useRef(null);
  const findOptionByValue = useCallback(
    (val) => {
      const optionObject = options.find((opt) => {
        return usesIdBasedValues ? opt.id === val : opt.title === val;
      });
      return optionObject || { id: val, title: val };
    },
    [options, usesIdBasedValues]
  );

  // When the external selectedValue changes, make sure selectedOptions state follows.
  useEffect(() => {
    if (selectedValue?.length > 0 && options?.length > 0) {
      const initialSelectedOptions = selectedValue.map(findOptionByValue);
      setSelectedOptions(initialSelectedOptions);
    } else if (!selectedValue || selectedValue.length === 0) {
      setSelectedOptions([]);
    }
  }, [selectedValue, options, findOptionByValue]);

  // handle change (select/unselect). Keep your previous protections for search input clicks.
  const handleChange = (event, isSearch = false) => {
    const {
      target: { value },
    } = event;

    if (isSearch === true || isSearch?.key === '.$searchFieldInput') return;

    if (menuListRef.current) {
      scrollTopRef.current = menuListRef.current.scrollTop;
    }

    // detect special func (like create tag) based on last selected
    const processedValue = typeof value === 'string' ? value.split(',') : value;
    const lastVal = processedValue[processedValue.length - 1];
    const selectedCreateTag =
      type !== 'remove' &&
      (String(lastVal) === CREATE_TAG_ID || lastVal === CREATE_TAG_TITLE) &&
      (searchAutocomplete ||
        component === TAG_CREATE_COMPONENT ||
        component === TAG_CREATE_CONTACT_COMPONENT);

    if (processedValue.includes(PLACEHOLDER_OPTION_ID)) {
      setSelectedValue([]);
      return;
    }

    const selectedOption = options.find((opt) => {
      if (component === TAG_CREATE_COMPONENT) {
        return opt.title === lastVal;
      } else if (usesIdBasedValues) {
        return opt.id === lastVal;
      } else {
        return opt.title === lastVal;
      }
    });

    if (selectedCreateTag) {
      (selectedOption?.func || (() => setCreateTagModal(true)))();
      return;
    }

    if (selectedOption?.func) {
      // run custom func (create tag)
      selectedOption.func();
      return;
    }

    setSelectedValue(processedValue);
  };

  const handleMenuOpen = () => {
    setMenuWidth(getDropdownMenuWidth(fieldRootRef.current));

    // Clear any pending timeout from previous close
    if (snapshotClearTimeoutRef.current) {
      clearTimeout(snapshotClearTimeoutRef.current);
      snapshotClearTimeoutRef.current = null;
    }

    const shouldUseSnapshot = !(Number(maxRenderedOptions) > 0);

    selectedValuesSnapshotRef.current = shouldUseSnapshot ? [...(selectedValue || [])] : [];
    optionsSnapshotRef.current = shouldUseSnapshot ? [...(options || [])] : [];

    setHasSnapshot(shouldUseSnapshot);

    // Determine if divider should be shown based on snapshot
    if (shouldUseSnapshot) {
      const selectedValuesSet = new Set(selectedValuesSnapshotRef.current.map((v) => String(v)));
      const hasSelectedItems = selectedValuesSet.size > 0;
      const hasUnselectedItems = (options || []).some((opt) => {
        const val = usesIdBasedValues ? String(opt.id) : String(opt.title);
        return !selectedValuesSet.has(val);
      });
      shouldShowDividerRef.current = hasSelectedItems && hasUnselectedItems;
    } else {
      shouldShowDividerRef.current = false;
    }

    setMenuOpen(true);
    if (searchOnOpen && typeof onSearchChange === 'function') {
      try {
        const result = onSearchChange(search, { immediate: true });
        if (result && typeof result.then === 'function') {
          setInternalSearchLoading(true);
          Promise.resolve(result)
            .catch(() => {})
            .finally(() => setInternalSearchLoading(false));
        }
      } catch (_) {}
    }
    // don't clear search here; user may want it preserved while menu is open
    // restore scroll later in layout effect
  };

  const handleMenuClose = useCallback(() => {
    // update selectedOptions to reflect full objects
    if (selectedValue?.length > 0) {
      const newSelectedOptions = selectedValue.map(findOptionByValue);
      setSelectedOptions(newSelectedOptions);
    } else {
      setSelectedOptions([]);
    }

    setMenuOpen(false);
    setSearch(''); // clear search when closing

    // Clear snapshot after a delay to allow close animation to complete
    // This prevents items from moving/reordering during the close animation
    // The snapshot state will persist until the animation finishes
    if (snapshotClearTimeoutRef.current) {
      clearTimeout(snapshotClearTimeoutRef.current);
    }
    snapshotClearTimeoutRef.current = setTimeout(() => {
      setHasSnapshot(false);
      selectedValuesSnapshotRef.current = [];
      optionsSnapshotRef.current = [];
      shouldShowDividerRef.current = false;
      snapshotClearTimeoutRef.current = null;
    }, 300); // 300ms should be enough for most close animations
  }, [selectedValue, findOptionByValue]);

  const handleClear = useCallback(
    (event) => {
      event.stopPropagation();
      setSelectedValue([]);
      setSelectedOptions([]);
      // Reset search and notify parent; show loader if async
      setSearch('');
      if (typeof onSearchChange === 'function') {
        try {
          const result = onSearchChange('');
          if (result && typeof result.then === 'function') {
            setInternalSearchLoading(true);
            Promise.resolve(result)
              .catch(() => {})
              .finally(() => setInternalSearchLoading(false));
          }
        } catch (_) {}
      }
    },
    [setSelectedValue, onSearchChange]
  );

  const handleSearchClear = useCallback(
    (event) => {
      event.stopPropagation();
      setSearch('');

      if (typeof onSearchChange !== 'function') return;

      try {
        const result = onSearchChange('');
        if (result && typeof result.then === 'function') {
          setInternalSearchLoading(true);
          Promise.resolve(result)
            .catch(() => {})
            .finally(() => setInternalSearchLoading(false));
        }
      } catch (_) {}
    },
    [onSearchChange]
  );

  const handleInlineOptionSelect = useCallback(
    (option) => {
      if (!option || option.disabled === true || option.isDivider) return;

      const itemValue = option.isPlaceholder
        ? PLACEHOLDER_OPTION_ID
        : usesIdBasedValues
          ? option.id
          : option.title;

      if (option.isPlaceholder) {
        selectedValuesSnapshotRef.current = [];
        setSelectedValue([]);
        return;
      }

      if (option.func) {
        option.func();
        return;
      }

      if (!multiple) {
        selectedValuesSnapshotRef.current = [itemValue];
        setSelectedValue([itemValue]);
        handleMenuClose();
        return;
      }

      const currentValue = Array.isArray(selectedValue) ? selectedValue : [];
      const nextValue = currentValue.includes(itemValue)
        ? currentValue.filter((value) => value !== itemValue)
        : [...currentValue, itemValue];

      selectedValuesSnapshotRef.current = nextValue;
      setSelectedValue(nextValue);
    },
    [handleMenuClose, multiple, selectedValue, setSelectedValue, usesIdBasedValues]
  );

  const clearIcon = useCallback(() => {
    if (!clearable || !selectedValue || selectedValue.length === 0) {
      return null;
    }

    return (
      <IconButton
        aria-label={`Clear ${placeHolderText || 'selected items'} selection`}
        className="selectClearIcon"
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={handleClear}
      >
        <img src={MultiSelectCrossCIcon} alt="" style={{ width: '11px', height: '11px' }} />
      </IconButton>
    );
  }, [clearable, selectedValue, handleClear, placeHolderText]);

  const renderSelectedOptions = useCallback(
    (selectedValue) => {
      return (
        <Box className="selectTooltipContent">
          {selectedValue?.map((item) => {
            const displayValue = getOptionLabel?.(item) ||
              (usesIdBasedValues
                ? options.find((option) => option.id === item)?.title || item
                : item);
            return <Typography key={item}>{displayValue}</Typography>;
          })}
        </Box>
      );
    },
    [getOptionLabel, options, usesIdBasedValues]
  );

  const getSelectedDisplayValue = useCallback(
    (item) => {
      return getOptionLabel?.(item) ||
        (usesIdBasedValues
          ? options.find((option) => option.id === item)?.title || item
          : item);
    },
    [getOptionLabel, options, usesIdBasedValues]
  );

  const selectedPreviewText = useMemo(() => {
    if (!selectedPreviewLimit || !Array.isArray(selectedValue) || selectedValue.length === 0) {
      return '';
    }

    const selectedLabels = selectedValue.map(getSelectedDisplayValue);
    const previewLabels = selectedLabels.slice(0, selectedPreviewLimit);
    return selectedLabels.length > selectedPreviewLimit
      ? `${previewLabels.join(', ')}, ...`
      : previewLabels.join(', ');
  }, [getSelectedDisplayValue, selectedPreviewLimit, selectedValue]);

  // Compute filteredOptions deterministically from options, search and selectedValue
  const filteredOptions = useMemo(() => {
    const searchLower = (search || '').toLowerCase();

    const filterBySearch = (item) => {
      if (item?.isDivider) return true;
      const searchableText = `${item?.title || ''} ${item?.id || ''}`;
      return searchableText.toLowerCase().includes(searchLower);
    };

    // Filter options by search
    const optionsInSearch = (options || []).filter(filterBySearch);

    let final = [];

    // If we have a snapshot (menu was opened), use it to maintain item positions
    // This prevents items from moving during close animation or when selecting/unselecting
    // The snapshot was captured when menu opened, so items stay in their sections even if selection changes
    if (hasSnapshot) {
      // Use snapshot to determine which items belong in which section
      const snapshotSelectedSet = new Set(selectedValuesSnapshotRef.current.map((v) => String(v)));

      // Create a set of snapshot options to detect newly added options
      const snapshotOptionsSet = new Set(
        optionsSnapshotRef.current.map((opt) =>
          usesIdBasedValues ? String(opt.id) : String(opt.title)
        )
      );

      // Create a map to maintain original order from the options snapshot (not just filtered)
      const optionIndexMap = new Map();
      (optionsSnapshotRef.current || []).forEach((item, index) => {
        const val = usesIdBasedValues ? String(item.id) : String(item.title);
        optionIndexMap.set(val, index);
      });

      // Helper function to sort by original order in options array
      const sortByOriginalOrder = (a, b) => {
        const valA = usesIdBasedValues ? String(a.id) : String(a.title);
        const valB = usesIdBasedValues ? String(b.id) : String(b.title);
        const indexA = optionIndexMap.get(valA) ?? Number.MAX_SAFE_INTEGER;
        const indexB = optionIndexMap.get(valB) ?? Number.MAX_SAFE_INTEGER;
        return indexA - indexB;
      };

      // Separate existing items based on snapshot (not current selection)
      const selectedInSnapshot = optionsInSearch.filter((item) => {
        const val = usesIdBasedValues ? String(item.id) : String(item.title);
        // Include items that were in snapshot AND were selected in snapshot
        return snapshotOptionsSet.has(val) && snapshotSelectedSet.has(val);
      });

      const unselectedInSnapshot = optionsInSearch.filter((item) => {
        const val = usesIdBasedValues ? String(item.id) : String(item.title);
        // Include items that were in snapshot AND were NOT selected in snapshot
        return snapshotOptionsSet.has(val) && !snapshotSelectedSet.has(val);
      });

      // Get all newly added options (options that exist now but weren't in snapshot)
      // All newly added options should appear in the unselected section in their natural position
      // regardless of whether they are selected or not
      const newlyAddedOptions = optionsInSearch.filter((item) => {
        const val = usesIdBasedValues ? String(item.id) : String(item.title);
        return !snapshotOptionsSet.has(val);
      });

      // Merge: existing selected from snapshot stays in selected section
      // All other options (unselected from snapshot + all newly added) go to unselected section
      // Sort by original order so newly added options appear in their natural position
      const allSelected = [...selectedInSnapshot].sort(sortByOriginalOrder);
      const allUnselected = [...unselectedInSnapshot, ...newlyAddedOptions].sort(
        sortByOriginalOrder
      );

      // Determine if divider should be shown (if we have items on both sides)
      // Update divider flag if we have both selected and unselected items (including newly added)
      const hasSelectedItems = allSelected.length > 0;
      const hasUnselectedItems = allUnselected.length > 0;
      const shouldShowDivider = hasSelectedItems && hasUnselectedItems;

      if (shouldShowDivider) {
        final = [...allSelected, { id: '__divider__', isDivider: true }, ...allUnselected];
      } else {
        final = [...allSelected, ...allUnselected];
      }
    } else {
      // Menu is closed: use current selectedValue to compute arrangement normally
      const selectedObjs = (selectedValue || []).map(findOptionByValue).filter(Boolean);
      const selectedValuesSet = new Set((selectedValue || []).map((v) => String(v)));

      const selectedInSearch = selectedObjs.filter(filterBySearch);
      const unselected = optionsInSearch.filter((item) => {
        const val = usesIdBasedValues ? String(item.id) : String(item.title);
        return !selectedValuesSet.has(val);
      });

      if (selectedInSearch.length > 0 && unselected.length > 0) {
        final = [...selectedInSearch, { id: '__divider__', isDivider: true }, ...unselected];
      } else {
        final = [...selectedInSearch, ...unselected];
      }
    }

    // If nothing matches and we have autocomplete to create tag
    if (final.length === 0 && search?.trim() && searchAutocomplete && type !== 'remove') {
      final = [
        {
          id: CREATE_TAG_ID,
          title: CREATE_TAG_TITLE,
          custom: true,
          func: () => setCreateTagModal(true),
        },
      ];
    }

    if (placeholderOptionText && !search?.trim()) {
      return [
        {
          id: PLACEHOLDER_OPTION_ID,
          isPlaceholder: true,
          title: placeholderOptionText,
        },
        ...final,
      ];
    }

    return final;
  }, [
    options,
    search,
    selectedValue,
    findOptionByValue,
    usesIdBasedValues,
    searchAutocomplete,
    type,
    setCreateTagModal,
    hasSnapshot, // Include hasSnapshot to recalculate when snapshot state changes
    placeholderOptionText,
  ]);
  const renderedOptions = useMemo(() => {
    const limit = Number(maxRenderedOptions);
    if (!Number.isFinite(limit) || limit <= 0 || filteredOptions.length <= limit) {
      return filteredOptions;
    }

    return filteredOptions.slice(0, limit);
  }, [filteredOptions, maxRenderedOptions]);

  // maintain scroll position when menu opens/closes
  useLayoutEffect(() => {
    if (menuOpen && menuListRef.current) {
      menuListRef.current.scrollTop = scrollTopRef.current;
    }
  }, [menuOpen]);

  // focus search input when menu is open
  useEffect(() => {
    if (menuOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [menuOpen]);

  useEffect(() => {
    const nextFocusRequest = Number(searchFocusRequest) || 0;

    if (!nextFocusRequest || nextFocusRequest === lastSearchFocusRequestRef.current) return;

    lastSearchFocusRequestRef.current = nextFocusRequest;
    if (disabled) return;

    handleMenuOpen();
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [disabled, searchFocusRequest]);

  useEffect(() => {
    if (!inlineMenu || !menuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (selectWrapperRef.current?.contains(event.target)) return;

      handleMenuClose();
    };

    document.addEventListener('pointerdown', handleOutsideClick);

    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, [handleMenuClose, inlineMenu, menuOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (snapshotClearTimeoutRef.current) {
        clearTimeout(snapshotClearTimeoutRef.current);
      }
    };
  }, []);

  const effectiveSearchLoading =
    (typeof searchLoading === 'boolean' ? searchLoading : internalSearchLoading) && menuOpen;
  const searchPlaceholder = getDropdownSearchPlaceholder(
    searchFieldPlaceholderText,
    placeHolderText
  );
  const emptyCreateTagOption = filteredOptions?.find(
    (option) =>
      String(option?.id) === CREATE_TAG_ID ||
      option?.title === CREATE_TAG_TITLE ||
      typeof option?.func === 'function'
  ) || {
    id: CREATE_TAG_ID,
    title: CREATE_TAG_TITLE,
    custom: true,
    func: () => setCreateTagModal(true),
  };
  const normalizedTagOptions = (filteredOptions || []).filter(
    (option) => !option?.isDivider && option?.title !== NO_TAGS_ASSIGNED_TITLE
  );
  const shouldShowEmptyTagsState =
    !search?.trim() &&
    type !== 'remove' &&
    (searchAutocomplete ||
      component === TAG_CREATE_COMPONENT ||
      component === TAG_CREATE_CONTACT_COMPONENT) &&
    (filteredOptions?.length === 0 ||
      (normalizedTagOptions.length <= 1 &&
        normalizedTagOptions.every(
          (option) =>
            String(option?.id) === CREATE_TAG_ID ||
            option?.title === CREATE_TAG_TITLE ||
            typeof option?.func === 'function'
        )));

  const selectedValueContent = (
    <Box className="selectInnerContentWrapper">
      <Typography
        component="div"
        className={`selectInnerContent ${selectedValue?.length > 0 ? 'selectedValue' : ''}`}
      >
        <Typography className="selectInnerContentLabel">
          {selectedPreviewText || placeHolderText}
        </Typography>
        {!selectedPreviewText && (selectedValue?.length > 0 || activeState) && (
          <OlcTooltip
            title={renderSelectedOptions(selectedValue)}
            disableHoverListener={selectedValue?.length === 0 ? true : false}
            placement="bottom-start"
          >
            <span
              className="selectInnerContentCount"
              aria-label={`${selectedValue?.length || 0} selected`}
            >
              {selectedValue?.length}
            </span>
          </OlcTooltip>
        )}
      </Typography>
    </Box>
  );
  const searchField = (
    <Box
      key="searchFieldInput"
      className="selectSearchInputWrapper"
    >
      {effectiveSearchLoading ? (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
            pointerEvents: 'none',
            backgroundColor: '#ED5C2F1F',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#ed5c2f',
            },
          }}
        />
      ) : null}
      <Input
        placeholder={searchPlaceholder}
        fullWidth
        inputRef={searchInputRef}
        onChange={(e) => {
          const value = e.target.value;
          setSearch(value);
          try {
            const result = onSearchChange(value);
            if (result && typeof result.then === 'function') {
              setInternalSearchLoading(true);
              Promise.resolve(result)
                .catch(() => {})
                .finally(() => setInternalSearchLoading(false));
            }
          } catch (_) {}
        }}
        value={search}
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            handleMenuClose();
            focusAdjacentField(fieldRootRef.current, e.shiftKey);
            return;
          }
          e.stopPropagation();
        }}
        onClick={(e) => handleChange(e, true)}
        key="searchFieldInput"
        id={searchInputId}
        className="selectSearchInput"
      />
      {search.length > 0 && (
        <img
          src={MultiSelectCrossCIcon}
          alt="clear"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={handleSearchClear}
          style={{ cursor: 'pointer' }}
        />
      )}
    </Box>
  );
  const optionItems = renderedOptions?.length > 0 ? (
    renderedOptions.map((option) => {
      const {
        id,
        title,
        disabled: optionDisabled,
        icon,
        isDivider,
        isPlaceholder,
      } = option;

      if (isDivider) {
        return <Divider className="searchableMenuDivider" key={id} />;
      }

      const itemValue = isPlaceholder
        ? PLACEHOLDER_OPTION_ID
        : usesIdBasedValues
          ? id
          : title;
      const isCreateTagOption = id === CREATE_TAG_ID;
      const isChecked = isPlaceholder
        ? (selectedValue || []).length === 0
        : (selectedValue || []).indexOf(itemValue) > -1;

      return (
        <MenuItem
          disabled={optionDisabled === true}
          disableRipple
          className={[
            isCreateTagOption ? 'searchableMenuAction createNewTagOption' : '',
            isPlaceholder ? 'placeholderOption' : '',
          ].filter(Boolean).join(' ')}
          key={String(id)}
          onClick={() => handleInlineOptionSelect(option)}
          value={itemValue}
        >
          {icon && customIcon ? (
            <img src={icon} alt="icon" style={{ padding: '10px' }} />
          ) : (
            !simpleSelect &&
            !isCreateTagOption && (
              <Checkbox
                checked={isChecked}
                icon={<img src={UncheckedCIcon} alt="unchecked" />}
                checkedIcon={<img src={CheckedCIcon} alt="checked" />}
              />
            )
          )}
          <ListItemText
            primary={<TruncateTextFixedLength text={title} maxLength={20} />}
            className={isCreateTagOption ? 'createNewTagOptionText' : ''}
          />
        </MenuItem>
      );
    })
  ) : (
    <MenuItem disabled className="noOptionsText">
      <Typography>{noOptionText}</Typography>
    </MenuItem>
  );

  if (inlineMenu) {
    return (
      <FormControl ref={selectWrapperRef} className={`multiSelectRedesign inlineMenuSelect ${className}`}>
        <Box
          aria-expanded={menuOpen ? 'true' : 'false'}
          aria-haspopup="listbox"
          className="basicSelect inlineBasicSelect"
          onClick={() => {
            if (disabled) return;

            if (menuOpen) {
              handleMenuClose();
            } else {
              handleMenuOpen();
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (menuOpen) {
                handleMenuClose();
              } else {
                handleMenuOpen();
              }
            }

            if (event.key === 'Escape') {
              handleMenuClose();
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
        >
          {selectedValueContent}
          {clearIcon()}
          <img
            className={menuOpen ? 'inlineSelectArrow open' : 'inlineSelectArrow'}
            src={MultiSelectArrowCIcon}
            alt="select-arrow"
          />
        </Box>

        {menuOpen && (
          <Box
            className={`selectOptions olcSearchableMenu inlineSelectOptions ${component === TAG_CREATE_COMPONENT ? 'tagSelectOptions' : ''}`}
            onMouseDown={(event) => event.stopPropagation()}
            role="listbox"
          >
            {searchField}
            <Box className="inlineSelectOptionsBody" ref={menuListRef}>
              {optionItems}
            </Box>
          </Box>
        )}
      </FormControl>
    );
  }

  return (
    <FormControl ref={fieldRootRef} className={`olcMultiSelect multiSelectRedesign ${className}`}>
      <Select
        id={selectId}
        inputProps={{
          'aria-label': placeHolderText || searchFieldPlaceholderText || 'Select options',
        }}
        multiple={multiple}
        displayEmpty
        open={menuOpen}
        value={selectedValue}
        disabled={disabled}
        onChange={handleChange}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        input={<OutlinedInput />}
        renderValue={() => {
          return selectedValueContent;
        }}
        MenuProps={{
          ...MenuProps,
          disableScrollLock: true,
          PaperProps: {
            ...MenuProps.PaperProps,
            style: {
              ...MenuProps.PaperProps.style,
              ...getDropdownMenuWidthStyles(menuWidth),
            },
          },
          classes: {
            paper: [
              'selectOptions',
              'olcSearchableMenu',
              component === TAG_CREATE_COMPONENT ? 'tagSelectOptions' : '',
              menuPaperClassName,
            ]
              .filter(Boolean)
              .join(' '),
          },
          MenuListProps: {
            ref: menuListRef,
            onMouseDown: (e) => e.stopPropagation(),
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              overflowY: 'auto',
            },
          },
        }}
        IconComponent={(props) => (
          <OlcDropdownChevron
            {...props}
            style={{
              ...props.style,
              top: '42%',
              right: '20px',
            }}
          />
        )}
        className="basicSelect"
      >
        {searchField}

        {shouldShowEmptyTagsState ? (
          [
            <MenuItem disabled className="noTagsOption" key="no-tags">
              <ListItemText primary="No tags" />
            </MenuItem>,
            <Divider className="searchableMenuDivider" key="no-tags-divider" />,
            <MenuItem
              disableRipple
              className="searchableMenuAction emptyCreateNewTagOption"
              key={String(emptyCreateTagOption.id)}
              value={usesIdBasedValues ? emptyCreateTagOption.id : emptyCreateTagOption.title}
            >
              <ListItemText
                primary={
                  <TruncateTextFixedLength text={emptyCreateTagOption.title} maxLength={20} />
                }
                className="createNewTagOptionText"
              />
            </MenuItem>,
          ]
        ) : renderedOptions?.length > 0 ? (
          renderedOptions.map(({ id, title, disabled, icon, isDivider, isPlaceholder }) => {
            if (isDivider) {
              return <Divider className="searchableMenuDivider" key={id} />;
            }

            const itemValue = isPlaceholder
              ? PLACEHOLDER_OPTION_ID
              : usesIdBasedValues
                ? id
                : title;
            const isCreateTagOption = id === CREATE_TAG_ID;
            const isChecked = isPlaceholder
              ? (selectedValue || []).length === 0
              : (selectedValue || []).indexOf(itemValue) > -1;

            return (
              <MenuItem
                disabled={disabled === true}
                disableRipple
                className={[
                  isCreateTagOption ? 'searchableMenuAction createNewTagOption' : '',
                  isPlaceholder ? 'placeholderOption' : '',
                ].filter(Boolean).join(' ')}
                key={String(id)}
                value={itemValue}
              >
                {icon && customIcon ? (
                  <img src={icon} alt="icon" style={{ padding: '10px' }} />
                ) : (
                  !simpleSelect &&
                  !isCreateTagOption && (
                    <Checkbox
                      checked={isChecked}
                      icon={<img src={UncheckedCIcon} alt="unchecked" />}
                      checkedIcon={<img src={CheckedCIcon} alt="checked" />}
                    />
                  )
                )}
                <ListItemText
                  primary={<TruncateTextFixedLength text={title} maxLength={20} />}
                  className={isCreateTagOption ? 'createNewTagOptionText' : ''}
                />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled className="noOptionsText">
            <Typography>{noOptionText}</Typography>
          </MenuItem>
        )}
      </Select>
      {clearIcon()}
    </FormControl>
  );
};

export default MultiSelect;
